import { Box, BoxProps, keyframes, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";

interface IBybAnimatedBorder extends BoxProps {
  children: ReactNode;
  borderRadius: number | string;
  borderWidth?: number;
  animationDuration?: number;
  color?: string;
}

const rotate = keyframes`
  100% {
    transform: translate(-50%, -50%) rotate(1turn);
  }
`;

const buildCometGradient = (color: string) =>
  `conic-gradient(
    from 0deg,
    transparent 0deg,
    transparent 140deg,
    ${alpha(color, 0)} 160deg,
    ${alpha(color, 0.15)} 180deg,
    ${alpha(color, 0.5)} 200deg,
    ${color} 220deg,
    ${color} 360deg
  )`;

const BybAnimatedBorder = ({ children, borderRadius, borderWidth = 1, animationDuration = 4, color, sx, ...props }: IBybAnimatedBorder) => {
  const theme = useTheme();
  const resolvedColor = color ?? theme.palette.dsPurple?.v200 ?? theme.palette.dsSecondaryPurple.v200;

  return (
    <Box
      {...props}
      sx={{
        position: "relative",
        display: "inline-flex",
        borderRadius,
        flexShrink: 0,
        ...sx,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          borderRadius: "inherit",
          padding: `${borderWidth}px`,
          overflow: "hidden",
          pointerEvents: "none",
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          maskComposite: "exclude",
          cornerShape: "inherit",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(0deg)",
            width: "99999px",
            height: "99999px",
            backgroundImage: buildCometGradient(resolvedColor),
            animation: `${rotate} ${animationDuration}s linear infinite`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              backgroundImage: "none",
              backgroundColor: resolvedColor,
            },
          },
        }}
      />
      <Box position="relative" borderRadius="inherit" flex={1} minWidth={0}>
        {children}
      </Box>
    </Box>
  );
};

export default BybAnimatedBorder;
